import * as THREE from 'three';
import { TransactionShip, ShipClass } from './TransactionShip';

export interface PathOptions {
  avoidanceRadius?: number; // Radius around sun to avoid
  curveIntensity?: number; // How curved the path should be (0-1)
  randomization?: number; // Amount of random variation (0-1)
}

export class TrajectoryManager {
  private sunPosition: THREE.Vector3;
  private avoidanceRadius: number;
  private batchUpdateThreshold: number;

  constructor(sunPosition: THREE.Vector3 = new THREE.Vector3(0, 0, 0)) {
    this.sunPosition = sunPosition;
    this.avoidanceRadius = 15; // Default sun avoidance radius
    this.batchUpdateThreshold = 100; // Batch updates for > 100 ships
  }

  public calculatePath(
    origin: THREE.Vector3,
    destination: THREE.Vector3,
    shipClass: ShipClass,
    options: PathOptions = {}
  ): THREE.CatmullRomCurve3 {
    const {
      avoidanceRadius = this.avoidanceRadius,
      curveIntensity = this.getCurveIntensityForClass(shipClass),
      randomization = 0.1
    } = options;

    // Validate inputs
    if (!origin || !destination) {
      console.warn('TrajectoryManager: Invalid origin or destination provided, creating fallback path');
      return new THREE.CatmullRomCurve3([
        new THREE.Vector3(0, 0, 0),
        new THREE.Vector3(1, 0, 0)
      ]);
    }

    // Check if origin and destination are too close (would result in zero-length path)
    const distance = origin.distanceTo(destination);
    if (distance < 0.1) {
      console.warn('TrajectoryManager: Origin and destination too close, creating minimum viable path');
      const direction = new THREE.Vector3(1, 0, 0); // Default direction
      const endPoint = origin.clone().add(direction.multiplyScalar(2));
      return new THREE.CatmullRomCurve3([origin.clone(), endPoint]);
    }

    // Calculate control points for curved path
    const midpoint = new THREE.Vector3()
      .addVectors(origin, destination)
      .multiplyScalar(0.5);
    
    // Check if path intersects sun
    const pathToSun = new THREE.Vector3().subVectors(midpoint, this.sunPosition);
    const distanceToSun = pathToSun.length();
    
    const controlPoints: THREE.Vector3[] = [origin.clone()];
    
    if (distanceToSun < avoidanceRadius) {
      // Path goes through sun, create avoidance path
      const avoidanceVector = pathToSun.normalize().multiplyScalar(avoidanceRadius * 1.2);
      
      // Create control points that curve around the sun
      const tangent = new THREE.Vector3()
        .crossVectors(avoidanceVector, new THREE.Vector3(0, 1, 0))
        .normalize();
      
      // Add curve based on ship class (higher fee = more direct route)
      const curveAmount = (1 - curveIntensity) * avoidanceRadius;
      
      const control1 = midpoint.clone()
        .add(avoidanceVector)
        .add(tangent.clone().multiplyScalar(curveAmount * (Math.random() - 0.5) * randomization));
      
      const control2 = midpoint.clone()
        .add(avoidanceVector)
        .add(tangent.clone().multiplyScalar(-curveAmount * (Math.random() - 0.5) * randomization));
      
      controlPoints.push(control1);
      controlPoints.push(control2);
    } else {
      // Direct path with slight curve for visual interest
      const direction = new THREE.Vector3().subVectors(destination, origin);
      const perpendicular = new THREE.Vector3()
        .crossVectors(direction, new THREE.Vector3(0, 1, 0))
        .normalize();
      
      // Ensure we have a valid perpendicular vector
      if (perpendicular.length() < 0.1) {
        // If direction is parallel to Y-axis, use X-axis as reference
        perpendicular.crossVectors(direction, new THREE.Vector3(1, 0, 0)).normalize();
      }
      
      const curveOffset = perpendicular.multiplyScalar(
        curveIntensity * Math.max(5, distance * 0.1) * (Math.random() - 0.5)
      );
      
      const control = midpoint.clone().add(curveOffset);
      controlPoints.push(control);
    }
    
    controlPoints.push(destination.clone());
    
    // Create smooth curve through control points
    const curve = new THREE.CatmullRomCurve3(controlPoints);
    curve.curveType = 'catmullrom';
    curve.tension = 0.5;
    
    // Validate the curve has a reasonable length
    const curveLength = curve.getLength();
    if (curveLength < 0.1) {
      console.warn('TrajectoryManager: Generated curve too short, creating fallback path');
      // Create a simple direct path with minimum length
      const direction = new THREE.Vector3().subVectors(destination, origin).normalize();
      if (direction.length() < 0.1) {
        direction.set(1, 0, 0); // Fallback direction
      }
      const extendedEnd = origin.clone().add(direction.multiplyScalar(Math.max(2, distance)));
      return new THREE.CatmullRomCurve3([origin.clone(), extendedEnd]);
    }
    
    return curve;
  }

  public updatePosition(ship: TransactionShip, deltaTime: number, timeScale: number = 1.0): void {
    ship.updatePosition(deltaTime, timeScale);
    
    // Check if ship has arrived
    if (ship.trajectory.progress >= 1.0) {
      this.onArrival(ship);
    }
  }

  public updateBatch(ships: TransactionShip[], deltaTime: number, timeScale: number = 1.0): void {
    // Use batch processing for better performance with many ships
    const activeShips = ships.filter(ship => ship.isActive && ship.trajectory.progress < 1.0);
    
    if (activeShips.length < this.batchUpdateThreshold) {
      // Update individually for small batches
      activeShips.forEach(ship => this.updatePosition(ship, deltaTime, timeScale));
    } else {
      // Batch update for large numbers
      this.performBatchUpdate(activeShips, deltaTime, timeScale);
    }
  }

  private performBatchUpdate(ships: TransactionShip[], deltaTime: number, timeScale: number): void {
    // Group ships by similar speed for efficient batch processing
    const speedGroups = new Map<number, TransactionShip[]>();
    
    ships.forEach(ship => {
      const speedKey = Math.round(ship.baseSpeed * 10); // Group by speed to nearest 0.1
      if (!speedGroups.has(speedKey)) {
        speedGroups.set(speedKey, []);
      }
      speedGroups.get(speedKey)!.push(ship);
    });
    
    // Update each speed group
    speedGroups.forEach((group, speedKey) => {
      const speed = speedKey / 10;
      const increment = (speed * timeScale * deltaTime);
      
      group.forEach(ship => {
        // Use the ship's own updatePosition method which has all the validation
        // Instead of duplicating validation logic here
        ship.updatePosition(deltaTime, timeScale);
      });
    });
  }

  private getCurveIntensityForClass(shipClass: ShipClass): number {
    const intensities: Record<ShipClass, number> = {
      micro: 0.8,    // Most curved path
      standard: 0.6,  // Moderate curve
      priority: 0.3,  // Less curved
      whale: 0.1      // Almost direct
    };
    
    return intensities[shipClass];
  }

  private onArrival(ship: TransactionShip): void {
    // This will be overridden by the system managing the ships
    // For now, just deactivate the ship
    ship.deactivate();
  }

  public setSunPosition(position: THREE.Vector3): void {
    this.sunPosition.copy(position);
  }

  public setAvoidanceRadius(radius: number): void {
    this.avoidanceRadius = radius;
  }
}